import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  MoreHorizontal,
  Plus,
  Users,
  X as XIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/api/client";
import {
  addGroupMembers,
  createGroup,
  deleteGroup,
  getGroup,
  listGroups,
  patchGroup,
  removeGroupMember,
} from "@/api/groups";
import type { GroupOut, MailboxResponse, UserSummary } from "@/api/schemas";
import { listOrgUsers } from "@/api/users";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableBodySkeleton } from "@/components/ui/skeleton";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";

const GROUPS_QUERY = ["groups"] as const;
const ORG_USERS_QUERY = ["org-users"] as const;

function GroupsAdminPage() {
  const { data: groups, isLoading } = useQuery({
    queryKey: GROUPS_QUERY,
    queryFn: listGroups,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<GroupOut | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GroupOut | null>(null);
  const [membersTarget, setMembersTarget] = useState<GroupOut | null>(null);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium tracking-tight">Groups</h1>
          <p className="mt-1 text-sm text-text-muted">
            Groups bundle agents into teams. Connect a group to a mailbox so its
            members service incoming tickets, and use groups in assignment
            rules.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Create group
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-surface">
        {isLoading ? (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Description</TH>
                <TH>Members</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TableBodySkeleton rows={4} cells={4} />
          </Table>
        ) : !groups || groups.length === 0 ? (
          <EmptyState />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Description</TH>
                <TH>Members</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TBody>
              {groups.map((g) => (
                <TR key={g.id}>
                  <TD className="font-medium">{g.name}</TD>
                  <TD className="text-text-muted">
                    {g.description || "—"}
                  </TD>
                  <TD>
                    <button
                      type="button"
                      className="text-xs text-link hover:underline"
                      onClick={() => setMembersTarget(g)}
                    >
                      {g.member_count}{" "}
                      {g.member_count === 1 ? "member" : "members"}
                    </button>
                  </TD>
                  <TD>
                    <RowActionMenu
                      onEdit={() => setEditTarget(g)}
                      onMembers={() => setMembersTarget(g)}
                      onDelete={() => setDeleteTarget(g)}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditDialog
        group={editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
      />
      <DeleteDialog
        group={deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />
      <MembersDialog
        group={membersTarget}
        onOpenChange={(o) => !o && setMembersTarget(null)}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
        <Users className="h-4 w-4 text-text-muted" />
      </div>
      <div className="text-sm font-medium">No groups yet</div>
      <div className="mt-1 text-xs text-text-muted">
        Click <span className="font-mono">Create group</span> to add one.
      </div>
    </div>
  );
}

function RowActionMenu({
  onEdit,
  onMembers,
  onDelete,
}: {
  onEdit: () => void;
  onMembers: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuItem onSelect={onMembers}>Manage members</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={onDelete}>
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type FormValues = { name: string; description: string };

function CreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({
    defaultValues: { name: "", description: "" },
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      createGroup({
        name: v.name.trim(),
        description: v.description.trim() || null,
      }),
    onSuccess: (created) => {
      toast.success(`Created ${created.name}`);
      void qc.invalidateQueries({ queryKey: GROUPS_QUERY });
      onOpenChange(false);
      form.reset({ name: "", description: "" });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "group_name_conflict") {
        toast.error("A group with this name already exists");
      } else {
        toast.error(e instanceof ApiError ? e.message : "Could not create group");
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) form.reset({ name: "", description: "" });
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create group</DialogTitle>
          <DialogDescription>
            Pick a short name. Add agents from the Manage members dialog after.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="flex flex-col gap-3"
        >
          <FormField label="Name">
            <Input
              {...form.register("name", {
                required: true,
                minLength: 1,
                maxLength: 80,
              })}
              placeholder="Tier 1"
              autoFocus
            />
          </FormField>
          <FormField label="Description (optional)">
            <Input
              {...form.register("description", { maxLength: 500 })}
              placeholder="Frontline support"
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  group,
  onOpenChange,
}: {
  group: GroupOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({
    values: group
      ? { name: group.name, description: group.description ?? "" }
      : undefined,
  });

  const mutation = useMutation({
    mutationFn: (body: { name?: string; description?: string | null }) => {
      if (!group) throw new Error("no group");
      return patchGroup(group.id, body);
    },
    onSuccess: (updated) => {
      toast.success(`Updated ${updated.name}`);
      void qc.invalidateQueries({ queryKey: GROUPS_QUERY });
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "group_name_conflict") {
        toast.error("A group with this name already exists");
      } else {
        toast.error(e instanceof ApiError ? e.message : "Could not update group");
      }
    },
  });

  if (!group) return null;

  return (
    <Dialog open={!!group} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit group</DialogTitle>
          <DialogDescription>{group.name}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => {
            const body: { name?: string; description?: string | null } = {};
            const cleanName = v.name.trim();
            const cleanDesc = v.description.trim();
            if (cleanName !== group.name) body.name = cleanName;
            const currentDesc = group.description ?? "";
            if (cleanDesc !== currentDesc) {
              body.description = cleanDesc || null;
            }
            if (Object.keys(body).length === 0) {
              onOpenChange(false);
              return;
            }
            mutation.mutate(body);
          })}
          className="flex flex-col gap-3"
        >
          <FormField label="Name">
            <Input
              {...form.register("name", {
                required: true,
                minLength: 1,
                maxLength: 80,
              })}
              autoFocus
            />
          </FormField>
          <FormField label="Description (optional)">
            <Input {...form.register("description", { maxLength: 500 })} />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  group,
  onOpenChange,
}: {
  group: GroupOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => {
      if (!group) throw new Error("no group");
      return deleteGroup(group.id);
    },
    onSuccess: () => {
      toast.success(`Deleted ${group?.name ?? "group"}`);
      void qc.invalidateQueries({ queryKey: GROUPS_QUERY });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not delete group");
    },
  });

  if (!group) return null;

  return (
    <Dialog open={!!group} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete group</DialogTitle>
          <DialogDescription>
            Delete <span className="font-medium">{group.name}</span>? Members
            stay in the org but are no longer associated with this group.
            Assignment rules that point at this group will stop matching.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({
  group,
  onOpenChange,
}: {
  group: GroupOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ["group", group?.id],
    queryFn: () => getGroup(group!.id),
    enabled: !!group,
  });
  const usersQuery = useQuery({
    queryKey: ORG_USERS_QUERY,
    queryFn: listOrgUsers,
    enabled: !!group,
  });

  const detail = detailQuery.data;

  const candidateUsers = useMemo<UserSummary[]>(() => {
    if (!detail || !usersQuery.data) return [];
    const memberIds = new Set(detail.members.map((m) => m.user_id));
    return usersQuery.data.filter((u) => !memberIds.has(u.id) && u.is_active);
  }, [detail, usersQuery.data]);

  const addMutation = useMutation({
    mutationFn: (userIds: string[]) => {
      if (!group) throw new Error("no group");
      return addGroupMembers(group.id, { user_ids: userIds });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["group", group?.id] });
      void qc.invalidateQueries({ queryKey: GROUPS_QUERY });
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not add member");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => {
      if (!group) throw new Error("no group");
      return removeGroupMember(group.id, userId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["group", group?.id] });
      void qc.invalidateQueries({ queryKey: GROUPS_QUERY });
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not remove member");
    },
  });

  if (!group) return null;

  return (
    <Dialog open={!!group} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage {group.name}</DialogTitle>
          <DialogDescription>
            Add agents to this group, then grant the group visibility on
            specific mailboxes — every group member sees those mailboxes'
            tickets via RLS.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div>
            <Label className="mb-2 block">Current members</Label>
            {detailQuery.isLoading ? (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : !detail || detail.members.length === 0 ? (
              <div className="text-xs text-text-muted">No members yet.</div>
            ) : (
              <ul className="flex flex-col gap-1">
                {detail.members.map((m) => (
                  <li
                    key={m.user_id}
                    className="flex items-center justify-between rounded border border-border px-2 py-1.5 text-xs"
                  >
                    <span>
                      <span className="font-medium">{m.name}</span>{" "}
                      <span className="text-text-muted">{m.email}</span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${m.name}`}
                      disabled={removeMutation.isPending}
                      onClick={() => removeMutation.mutate(m.user_id)}
                    >
                      <XIcon className="h-3 w-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Label className="mb-2 block">Add members</Label>
            <Command>
              <CommandInput placeholder="Search by name or email…" />
              <CommandList>
                <CommandEmpty>
                  {usersQuery.isLoading ? "Loading…" : "No matching users."}
                </CommandEmpty>
                <CommandGroup>
                  {candidateUsers.map((u) => (
                    <CommandItem
                      key={u.id}
                      value={`${u.name} ${u.email}`}
                      onSelect={() => addMutation.mutate([u.id])}
                      disabled={addMutation.isPending}
                    >
                      <span className="font-medium">{u.name}</span>
                      <span className="ml-2 text-text-muted">{u.email}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>

          <GroupMailboxVisibility groupId={group.id} />
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

// ---------------- Mailbox visibility ----------------

function GroupMailboxVisibility({ groupId }: { groupId: string }) {
  const qc = useQueryClient();
  const { data: granted } = useQuery({
    queryKey: ["group-mailbox-access", groupId],
    queryFn: () =>
      apiFetch<string[]>(`/api/groups/${groupId}/mailbox-access`),
  });
  const { data: myAccess } = useQuery({
    queryKey: ["mailbox-access", "me"],
    queryFn: () =>
      apiFetch<{ mailbox_ids: string[]; delegated_mailbox_ids: string[]; role: string }>(
        "/api/mailbox-access/me",
      ),
  });
  const { data: mailboxes } = useQuery({
    queryKey: ["mailboxes"],
    queryFn: () => apiFetch<MailboxResponse[]>("/api/mailboxes"),
  });

  const manageable = useMemo(() => {
    const allowed = new Set(myAccess?.delegated_mailbox_ids ?? []);
    return (mailboxes ?? []).filter((m) => allowed.has(m.id));
  }, [mailboxes, myAccess]);
  const grantedSet = useMemo(() => new Set(granted ?? []), [granted]);

  const grant = useMutation({
    mutationFn: (mailboxId: string) =>
      apiFetch<unknown>(`/api/groups/${groupId}/mailbox-access`, {
        method: "POST",
        json: { mailbox_id: mailboxId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["group-mailbox-access", groupId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed"),
  });
  const revoke = useMutation({
    mutationFn: (mailboxId: string) =>
      apiFetch<void>(`/api/groups/${groupId}/mailbox-access/${mailboxId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["group-mailbox-access", groupId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Failed"),
  });

  if (manageable.length === 0) {
    return (
      <div>
        <Label className="mb-2 block">Mailbox visibility</Label>
        <p className="text-xs text-text-muted">
          No mailboxes available — you're not authorised on any mailbox you
          could grant to this group.
        </p>
      </div>
    );
  }

  return (
    <div>
      <Label className="mb-1 block">Mailbox visibility</Label>
      <p className="mb-2 text-xs text-text-muted">
        Members of this group will be able to see tickets in the selected
        mailboxes (RLS-enforced).
      </p>
      <div className="flex flex-col gap-1">
        {manageable.map((m) => {
          const isGranted = grantedSet.has(m.id);
          return (
            <label key={m.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isGranted}
                onChange={(e) => {
                  if (e.target.checked) grant.mutate(m.id);
                  else revoke.mutate(m.id);
                }}
                className="h-4 w-4 rounded border-border"
              />
              <span>{m.display_name || m.email_address}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_auth/settings/groups")({
  component: GroupsAdminPage,
});
